/**
 * Qrew HR — Unified Profile Page (role-aware)
 *
 * Renders one of three skins from the same data source:
 *   - Employee Profile : large banner + lifecycle KPIs + Leave/Reporting/Assets/Holidays grid + Career timeline
 *   - Manager Profile  : team-centric KPIs + Team Structure + Current Priorities + Recognition + Manager Scorecard
 *   - HR/Admin Profile : operational ownership + Policies/Compliance/Onboarding/Payroll + Recent Activity + Team Collaboration
 *
 * Designed to match the reference screenshots while still consuming live tenant data.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { avatarFor } from "@/lib/avatar";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  CalendarDays, Mail, Phone, MapPin, BadgeCheck, Edit3, Star, Award, Briefcase,
  Users, FileText, Laptop, GraduationCap, Activity, Target, Sparkles, Shield,
  TrendingUp, AlertTriangle, ListTodo, Clock, ChevronRight, Building2, Hash,
} from "lucide-react";

const initials = (n = "U") => n.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");
const role2label = {
  super_admin: "Super Admin", hr_admin: "HR Admin", manager: "Manager",
  team_lead: "Team Lead", department_head: "Dept. Head", employee: "Employee",
};

function HeaderCard({ profile, role, onEdit }) {
  const isAdmin = ["super_admin", "hr_admin"].includes(role);
  const isManager = ["manager", "team_lead", "department_head"].includes(role);
  const subtitle = isAdmin ? "Operational ownership · People Ops" : isManager ? "Team commander · People leader" : "Personal workspace · Career hub";

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-violet-100 shadow-sm"
      style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 35%, #FAE8FF 75%, #FFE4E6 100%)" }}
      data-testid="profile-banner"
    >
      <div className="absolute -top-24 -left-20 w-80 h-80 rounded-full bg-violet-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-16 w-96 h-96 rounded-full bg-pink-200/35 blur-3xl pointer-events-none" />
      <div className="absolute top-10 right-1/3 w-40 h-40 rounded-full bg-cyan-100/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 p-6 sm:p-8 lg:p-10">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="relative shrink-0">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-white p-1 shadow-md">
              <Avatar className="w-full h-full rounded-[20px] overflow-hidden">
                <AvatarImage
                  src={(profile?.avatar_url && !profile.avatar_url.includes("dicebear.com")) ? profile.avatar_url : avatarFor(profile)}
                  alt={profile?.full_name}
                />
                <AvatarFallback className="rounded-[20px] bg-violet-100 text-violet-700 text-3xl font-bold">{initials(profile?.full_name)}</AvatarFallback>
              </Avatar>
            </div>
            <span className="absolute right-1 bottom-1 w-5 h-5 rounded-full bg-emerald-500 ring-4 ring-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-700/80">{subtitle}</div>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <h1 className="text-3xl sm:text-4xl font-[Outfit] font-bold tracking-tight truncate leading-tight text-slate-900">{profile?.full_name || "Profile"}</h1>
              <BadgeCheck className="w-6 h-6 text-violet-600" />
              <span className="text-[10px] font-bold bg-white/70 backdrop-blur border border-violet-100 text-violet-700 px-2.5 py-1 rounded-full uppercase tracking-wider">
                {role2label[role] || role}
              </span>
            </div>
            <div className="text-sm text-slate-700 mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{profile?.designation || "—"}</span>
              {profile?.department_name && <><span className="text-slate-400">·</span><span>{profile.department_name}</span></>}
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-2 text-[12px] text-slate-700">
              <div className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-violet-500" /><span className="font-mono">{profile?.id || "—"}</span></div>
              <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-violet-500" /><span className="truncate">{profile?.email || "—"}</span></div>
              <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-violet-500" /><span>{profile?.phone || "—"}</span></div>
              <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-violet-500" /><span>{profile?.location || "—"}</span></div>
              <div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-violet-500" /><span>{profile?.joining_date ? new Date(profile.joining_date).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—"}</span></div>
            </div>
          </div>

          <button onClick={onEdit} className="px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm shadow-md hover:shadow-lg hover:-translate-y-0.5 transition flex items-center gap-2 shrink-0" data-testid="profile-edit-btn">
            <Edit3 className="w-4 h-4" /> Edit Profile
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniKPI({ icon: Icon, tone = "violet", label, value, sub, hint, testid }) {
  const tones = {
    violet: "bg-violet-50 text-violet-600", cyan: "bg-cyan-50 text-cyan-600",
    emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600", blue: "bg-blue-50 text-blue-600",
  };
  return (
    <Card className="p-4 border-slate-200 shadow-sm bg-white" data-testid={testid}>
      <div className={`w-9 h-9 rounded-xl ${tones[tone]} flex items-center justify-center mb-2.5`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-2xl sm:text-3xl font-bold font-[Outfit] text-slate-900 leading-none mt-1.5 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1.5">{sub}</div>}
      {hint && <div className={`text-[10px] mt-1 font-bold ${hint.tone === "good" ? "text-emerald-600" : hint.tone === "bad" ? "text-rose-600" : "text-amber-600"}`}>{hint.label}</div>}
    </Card>
  );
}

/* =================================================================== EMPLOYEE */
function EmployeeProfileSkin({ profile, completion }) {
  const lb = profile?.leave_balance || {};
  return (
    <>
      {/* Profile completion bar */}
      <Card className="p-4 sm:p-5 border-violet-200 bg-gradient-to-r from-violet-50 to-white shadow-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative w-14 h-14 shrink-0">
            <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E2E8F0" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="url(#gradc)" strokeWidth="3" strokeDasharray={`${completion} 100`} strokeLinecap="round" />
              <defs>
                <linearGradient id="gradc" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2563EB" /><stop offset="50%" stopColor="#6D28D9" /><stop offset="100%" stopColor="#D946EF" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-[12px] font-bold text-slate-900">{completion}%</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold font-[Outfit] text-slate-900 text-sm sm:text-base">Profile Completion · {completion}%</div>
            <div className="text-[12px] text-slate-600">{completion >= 90 ? "Awesome! Almost there 🎉" : "Keep your profile updated for maximum impact"}</div>
          </div>
          <Link to="#" className="qh-btn btn-soft qh-btn-sm">Complete →</Link>
        </div>
      </Card>

      {/* Lifecycle KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MiniKPI icon={CalendarDays} tone="violet"  label="Years of Service" value={profile?.years_of_service || "1y"} sub={`Since ${profile?.joining_date ? new Date(profile.joining_date).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—"}`} testid="kpi-years" />
        <MiniKPI icon={CalendarDays} tone="amber"   label="Total Leaves" value={profile?.leaves_taken_this_year ?? 0} sub="this year" testid="kpi-leaves" />
        <MiniKPI icon={Star}         tone="emerald" label="Performance" value={profile?.performance_rating ?? "—"} hint={{ tone: "good", label: "Top 10%" }} testid="kpi-perf" />
        <MiniKPI icon={Target}       tone="cyan"    label="Next Appraisal" value={profile?.next_appraisal_label || "Oct 2026"} sub="upcoming" testid="kpi-appraisal" />
        <MiniKPI icon={GraduationCap}tone="rose"    label="Training" value={`${profile?.training_hours ?? 32}h`} sub="completed" testid="kpi-training" />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          {["overview", "personal", "job", "payroll", "bank", "documents", "skills", "assets", "leave", "team", "activity"].map(t => (
            <TabsTrigger key={t} value={t} className="capitalize">{t.replace("_", " ")}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* 4-column grid */}
          <div className="grid lg:grid-cols-4 gap-4">
            {/* Leave Balance */}
            <Card className="p-5 border-slate-200 shadow-sm bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold font-[Outfit] text-slate-900 text-sm">Leave Balance</div>
                <Link to="/leaves" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>
              </div>
              {[
                { code: "casual",  label: "Casual (CL)",  used: lb.casual ?? 12.5, total: 15, color: "bg-emerald-500" },
                { code: "sick",    label: "Sick (SL)",    used: lb.sick ?? 6,      total: 10, color: "bg-blue-500" },
                { code: "earned",  label: "Earned (EL)",  used: lb.earned ?? 18,   total: 20, color: "bg-violet-500" },
                { code: "comp",    label: "Comp Off (CO)",used: lb.comp_off ?? 3,  total: 5,  color: "bg-amber-500" },
              ].map(b => (
                <div key={b.code} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between text-[11.5px] mb-1">
                    <span className="text-slate-600 font-semibold">{b.label}</span>
                    <span className="text-slate-900 font-bold tabular-nums">{b.used} <span className="text-slate-400">/ {b.total} days</span></span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded overflow-hidden"><div className={`h-full ${b.color}`} style={{ width: `${Math.min(100, (b.used/b.total)*100)}%` }} /></div>
                </div>
              ))}
            </Card>

            {/* Reporting Structure */}
            <Card className="p-5 border-slate-200 shadow-sm bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold font-[Outfit] text-slate-900 text-sm">Reporting Structure</div>
                <Link to="/org-chart" className="text-[11px] font-semibold text-violet-700 hover:underline">View org chart</Link>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <Avatar className="w-8 h-8"><AvatarFallback className="bg-violet-100 text-violet-700 text-[10px]">{initials(profile?.manager_name || "Manager")}</AvatarFallback></Avatar>
                  <div className="text-left">
                    <div className="text-[12px] font-bold">{profile?.manager_name || "Reporting Manager"}</div>
                    <div className="text-[10px] text-slate-500">{profile?.manager_designation || "—"}</div>
                  </div>
                </div>
                <div className="my-1 h-3 w-px bg-slate-300 mx-auto" />
                <div className="inline-flex items-center gap-2 p-2 rounded-lg qh-grad-bg text-white">
                  <Avatar className="w-8 h-8 ring-2 ring-white/50">
                    {profile?.avatar_url && <AvatarImage src={profile.avatar_url} />}
                    <AvatarFallback className="bg-white/20 text-white text-[10px]">{initials(profile?.full_name)}</AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <div className="text-[12px] font-bold">{profile?.full_name}</div>
                    <div className="text-[10px] opacity-90">{profile?.designation}</div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Current Assets */}
            <Card className="p-5 border-slate-200 shadow-sm bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold font-[Outfit] text-slate-900 text-sm">Current Assets</div>
                <Link to="/assets" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>
              </div>
              {(profile?.assets || [
                { name: "MacBook Pro 14\"", category: "laptop", icon: Laptop },
                { name: "iPhone 15 Pro",    category: "phone",  icon: Hash },
                { name: "Dell 24\" Monitor", category: "monitor", icon: Hash },
              ]).slice(0, 4).map((a, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center"><Laptop className="w-3.5 h-3.5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold truncate">{a.name}</div>
                    <div className="text-[10px] text-slate-500">{a.assigned_on || "Assigned"}</div>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[9px]">In Use</Badge>
                </div>
              ))}
            </Card>

            {/* Upcoming Holidays */}
            <Card className="p-5 border-slate-200 shadow-sm bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold font-[Outfit] text-slate-900 text-sm">Upcoming Holidays</div>
                <Link to="/holidays" className="text-[11px] font-semibold text-violet-700 hover:underline">View calendar</Link>
              </div>
              {(profile?.upcoming_holidays || []).slice(0, 4).map((h) => (
                <div key={h.id || h.name} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
                  <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex flex-col items-center justify-center shrink-0">
                    <span className="text-[8px] font-bold uppercase">{h.date ? new Date(h.date).toLocaleDateString([], { month: "short" }) : "—"}</span>
                    <span className="text-sm font-bold leading-none">{h.date ? new Date(h.date).getDate() : "—"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold truncate">{h.name}</div>
                    <div className="text-[10px] text-slate-500">{h.date ? new Date(h.date).toLocaleDateString([], { weekday: "long" }) : ""}</div>
                  </div>
                </div>
              ))}
              {(!profile?.upcoming_holidays || profile.upcoming_holidays.length === 0) && (
                <div className="text-[12px] text-slate-400 text-center py-3">No upcoming holidays</div>
              )}
            </Card>
          </div>

          {/* Career timeline & Recent Activity */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5 border-slate-200 shadow-sm bg-white">
              <div className="font-bold font-[Outfit] text-slate-900 text-sm mb-3">Career Timeline</div>
              {(profile?.career || [
                { title: profile?.designation, company: "Current", date: "Now", current: true },
                { title: "Joined " + (profile?.designation || "the team"), company: profile?.department_name || "—", date: profile?.joining_date ? new Date(profile.joining_date).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—" },
              ]).map((c, i) => (
                <div key={i} className="flex gap-3 pb-3 last:pb-0 border-b border-slate-100 last:border-0">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full ${c.current ? "qh-grad-bg" : "bg-slate-300"} mt-1.5`} />
                    <div className="w-px flex-1 bg-slate-200 mt-1" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[12.5px] font-semibold text-slate-900">{c.title}</div>
                    <div className="text-[11px] text-slate-500">{c.company} · {c.date}</div>
                    {c.current && <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px] mt-1">Current</Badge>}
                  </div>
                </div>
              ))}
            </Card>
            <Card className="p-5 border-slate-200 shadow-sm bg-white">
              <div className="font-bold font-[Outfit] text-slate-900 text-sm mb-3">Recent Activity</div>
              {[
                { icon: FileText, label: "Updated profile information", time: "2h ago" },
                { icon: Clock,    label: "Submitted timesheet",         time: "5h ago" },
                { icon: CalendarDays, label: "Applied for leave (1d)",  time: "1d ago" },
                { icon: GraduationCap, label: "Completed UX course",    time: "2d ago" },
              ].map((a, i) => (
                <div key={i} className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0">
                  <div className="w-7 h-7 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><a.icon className="w-3.5 h-3.5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-slate-800">{a.label}</div>
                    <div className="text-[10px] text-slate-500">{a.time}</div>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        </TabsContent>

        {/* ========== PERSONAL TAB ========== */}
        <TabsContent value="personal" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Personal Information</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <Field label="Full Name"      value={profile?.full_name} />
              <Field label="Email"          value={profile?.email} />
              <Field label="Phone"          value={profile?.phone} />
              <Field label="Date of Birth"  value={profile?.dob ? new Date(profile.dob).toLocaleDateString() : null} />
              <Field label="Gender"         value={profile?.gender} cap />
              <Field label="Marital Status" value={profile?.marital_status} cap />
              <Field label="Address"        value={profile?.address} />
              <Field label="Location"       value={profile?.location} />
              <Field label="Emergency Contact" value={profile?.emergency_contact} />
            </div>
          </Card>
        </TabsContent>

        {/* ========== JOB TAB ========== */}
        <TabsContent value="job" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Job & Joining Details</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <Field label="Employee ID"     value={profile?.id} mono />
              <Field label="Designation"     value={profile?.designation} />
              <Field label="Department"      value={profile?.department_name} />
              <Field label="Reporting To"    value={profile?.manager_name} />
              <Field label="Joining Date"    value={profile?.joining_date ? new Date(profile.joining_date).toLocaleDateString() : null} />
              <Field label="Work Mode"       value={profile?.work_mode} cap />
              <Field label="Employee Type"   value={profile?.employee_type} cap />
              <Field label="Probation End"   value={profile?.probation_end ? new Date(profile.probation_end).toLocaleDateString() : "—"} />
              <Field label="Location"        value={profile?.location} />
            </div>
          </Card>
        </TabsContent>

        {/* ========== PAYROLL TAB ========== */}
        <TabsContent value="payroll" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Payroll & Statutory</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <Field label="CTC (Annual)"    value={profile?.ctc ? `₹${profile.ctc.toLocaleString()}` : null} />
              <Field label="PAN"             value={profile?.pan} mono />
              <Field label="Aadhaar"         value={profile?.aadhaar} mono />
              <Field label="UAN (PF)"        value={profile?.uan} mono />
              <Field label="ESI Number"      value={profile?.esi_number} mono />
              <Field label="Tax Regime"      value={profile?.tax_regime} cap />
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <Link to="/payroll" className="text-[12px] font-semibold text-violet-700 hover:underline">View payslips →</Link>
            </div>
          </Card>
        </TabsContent>

        {/* ========== BANK TAB ========== */}
        <TabsContent value="bank" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Bank Account</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <Field label="Bank Name"       value={profile?.bank_name} />
              <Field label="Account Number"  value={profile?.bank_account_number} mono />
              <Field label="IFSC Code"       value={profile?.bank_ifsc} mono />
              <Field label="Branch"          value={profile?.bank_branch} />
              <Field label="Account Holder"  value={profile?.bank_holder_name || profile?.full_name} />
            </div>
          </Card>
        </TabsContent>

        {/* ========== DOCUMENTS TAB ========== */}
        <TabsContent value="documents" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight">My Documents</div>
              <Link to="/documents" className="text-[11px] font-semibold text-violet-700 hover:underline">All documents →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { name: "Offer Letter",     t: "PDF" },
                { name: "Appointment Letter", t: "PDF" },
                { name: "Form 16 (FY 2024-25)", t: "PDF" },
                { name: "Latest Payslip",   t: "PDF" },
                { name: "PAN Card",         t: "Image" },
                { name: "Aadhaar Card",     t: "Image" },
              ].map(d => (
                <div key={d.name} className="p-3 rounded-xl border border-slate-100 hover:border-violet-200 transition flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><FileText className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900 truncate">{d.name}</div>
                    <div className="text-[10px] text-slate-500">{d.t}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* ========== SKILLS TAB ========== */}
        <TabsContent value="skills" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Skills & Expertise</div>
            {(profile?.skills || []).length === 0 ? (
              <div className="text-[12px] text-slate-500 py-6 text-center">No skills added yet. Click Edit Profile to add some.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(profile?.skills || []).map((s) => (
                  <span key={typeof s === "string" ? s : s.name} className="px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 text-[12px] font-semibold border border-violet-100">
                    {typeof s === "string" ? s : `${s.name} · ${s.level || ""}`.trim()}
                  </span>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ========== ASSETS TAB ========== */}
        <TabsContent value="assets" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Assigned Assets</div>
            {(profile?.assets || []).length === 0 ? (
              <div className="text-[12px] text-slate-500 py-6 text-center">No assets assigned. Visit Assets page for issuance.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {(profile?.assets || []).map(a => (
                  <div key={a.id} className="p-3 rounded-xl border border-slate-100 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{a.name}</div>
                      <div className="text-[10px] text-slate-500">{a.serial_number || a.category}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ========== LEAVE TAB ========== */}
        <TabsContent value="leave" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Leave Balance</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { code: "casual",  label: "Casual (CL)",   used: lb.casual ?? 0, total: 12, color: "bg-emerald-500" },
                { code: "sick",    label: "Sick (SL)",     used: lb.sick   ?? 0, total: 10, color: "bg-cyan-500" },
                { code: "earned",  label: "Earned (EL)",   used: lb.earned ?? 0, total: 20, color: "bg-violet-500" },
                { code: "comp",    label: "Comp Off (CO)", used: 0,              total: 5,  color: "bg-amber-500" },
              ].map(b => (
                <div key={b.code} className="p-4 rounded-2xl border border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{b.label}</div>
                  <div className="text-2xl font-bold font-[Outfit] text-slate-900 mt-1 tabular-nums">{b.used} <span className="text-sm font-normal text-slate-400">/ {b.total}</span></div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded overflow-hidden">
                    <div className={`h-full ${b.color}`} style={{ width: `${Math.min(100, (b.used / b.total) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-slate-100">
              <Link to="/leaves" className="text-[12px] font-semibold text-violet-700 hover:underline">Apply for leave →</Link>
            </div>
          </Card>
        </TabsContent>

        {/* ========== TEAM TAB ========== */}
        <TabsContent value="team" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">My Team</div>
            <div className="text-[12px] text-slate-500">Find your manager, peers and direct reports here.</div>
            <Link to="/people" className="mt-4 inline-flex px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold transition">Browse People →</Link>
          </Card>
        </TabsContent>

        {/* ========== ACTIVITY TAB ========== */}
        <TabsContent value="activity" className="mt-4">
          <Card className="p-6 border-slate-200 shadow-sm bg-white rounded-2xl">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight mb-4">Recent Activity</div>
            <div className="space-y-2.5">
              {[
                { icon: FileText,     label: "Updated profile information", time: "2h ago" },
                { icon: Clock,        label: "Submitted timesheet",         time: "5h ago" },
                { icon: CalendarDays, label: "Applied for leave (1d)",      time: "1d ago" },
                { icon: GraduationCap, label: "Completed UX course",        time: "2d ago" },
              ].map((a, i) => (
                <div key={i} className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0">
                  <div className="w-8 h-8 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0"><a.icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-800">{a.label}</div>
                    <div className="text-[10px] text-slate-500">{a.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bottom motivation strip */}
      <Card className="p-4 sm:p-5 border-violet-200 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-pink-50 shadow-sm flex items-center gap-3 flex-wrap">
        <div className="text-2xl">🚀</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 text-sm">You're doing amazing, {profile?.first_name || profile?.full_name?.split(" ")[0] || "there"}!</div>
          <div className="text-[12px] text-slate-600">Keep growing, keep inspiring. Your journey is just getting started.</div>
        </div>
        <button className="qh-btn btn-soft qh-btn-sm">View Growth Plan</button>
      </Card>
    </>
  );
}

/* Reusable label/value field used inside Profile detail tabs */
function Field({ label, value, mono, cap }) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`mt-0.5 text-[13px] ${mono ? "font-mono" : ""} ${cap ? "capitalize" : ""} ${empty ? "text-slate-400 italic" : "text-slate-900 font-semibold"}`}>
        {empty ? "—" : String(value)}
      </div>
    </div>
  );
}


/* =================================================================== MANAGER */
function ManagerProfileSkin({ profile }) {
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniKPI icon={Users}      tone="violet"  label="Direct Reports" value={profile?.team_size ?? 0} sub="View team" testid="kpi-reports" />
        <MiniKPI icon={ListTodo}   tone="amber"   label="Open Approvals" value={profile?.pending_approvals ?? 0} sub="View all" testid="kpi-approv" />
        <MiniKPI icon={Sparkles}   tone="cyan"    label="Active 1-on-1s" value={profile?.active_1on1s ?? 0} sub="View all" testid="kpi-1on1" />
        <MiniKPI icon={Star}       tone="emerald" label="Team Happiness" value={profile?.team_happiness ?? "—"} sub="Great" testid="kpi-happy" />
        <MiniKPI icon={Briefcase}  tone="rose"    label="Hiring Reqs"    value={profile?.open_hiring_requests ?? 0} testid="kpi-hire" />
        <MiniKPI icon={Target}     tone="blue"    label="Projects Owned" value={profile?.projects_owned ?? 0} testid="kpi-proj" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          {["overview", "team", "1-on-1 Notes", "performance", "recognition", "projects", "approvals", "documents", "skills", "activity"].map(t => (
            <TabsTrigger key={t} value={t} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-4 grid lg:grid-cols-3 gap-4">
          {/* Team Structure */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold font-[Outfit] text-slate-900 text-sm">Team Structure</div>
              <Link to="/people" className="text-[11px] font-semibold text-violet-700 hover:underline">View full team</Link>
            </div>
            <div className="text-center">
              <div className="inline-block p-2.5 rounded-xl qh-grad-bg text-white text-[12px] font-bold shadow-md">{profile?.full_name}<br/><span className="text-[10px] font-semibold opacity-90">{profile?.designation}</span></div>
              <div className="my-2 h-3 w-px bg-slate-300 mx-auto" />
              <div className="grid grid-cols-3 gap-2">
                {(profile?.team_preview || [{full_name: "—"}]).slice(0,3).map((t,i) => (
                  <div key={i} className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                    <Avatar className="w-8 h-8 mx-auto mb-1"><AvatarFallback className="bg-violet-100 text-violet-700 text-[10px]">{initials(t.full_name)}</AvatarFallback></Avatar>
                    <div className="text-[10px] font-semibold truncate">{t.full_name}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-slate-500">+{Math.max(0, (profile?.team_size ?? 0) - 3)} more reports</div>
            </div>
          </Card>

          {/* Current Priorities */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white">
            <div className="font-bold font-[Outfit] text-slate-900 text-sm mb-3">Current Priorities</div>
            {[
              { title: "Deliver Q2 Platform Release", sub: "Engineering Roadmap", status: "On Track", tone: "emerald", pct: 78 },
              { title: "Improve Code Quality", sub: "Engineering Excellence", status: "At Risk", tone: "amber", pct: 42 },
              { title: "Build High Performance Culture", sub: "People & Culture", status: "On Track", tone: "emerald", pct: 65 },
              { title: "Reduce Cycle Time by 20%", sub: "Delivery Efficiency", status: "In Progress", tone: "violet", pct: 30 },
            ].map((p, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[12px] font-semibold text-slate-900">{p.title}</div>
                  <Badge className={`text-[9px] border-0 ${p.tone === "emerald" ? "bg-emerald-100 text-emerald-700" : p.tone === "amber" ? "bg-amber-100 text-amber-700" : "bg-violet-100 text-violet-700"}`}>{p.status}</Badge>
                </div>
                <div className="text-[10px] text-slate-500 mb-1">{p.sub}</div>
                <div className="h-1.5 bg-slate-100 rounded overflow-hidden"><div className={`h-full ${p.tone === "emerald" ? "bg-emerald-500" : p.tone === "amber" ? "bg-amber-500" : "qh-grad-bg"}`} style={{ width: `${p.pct}%` }} /></div>
              </div>
            ))}
          </Card>

          {/* Recent Recognition */}
          <Card className="p-5 border-slate-200 shadow-sm bg-white">
            <div className="font-bold font-[Outfit] text-slate-900 text-sm mb-3">Recent Recognition</div>
            {(profile?.recognition || [
              { from: "Vikram S.", text: "Outstanding leadership on the Platform Upgrade!", chip: "Above & Beyond" },
              { from: "Neha K.", text: "Great job facilitating cross-team alignment.", chip: "Collaboration Star" },
              { from: "DevOps Team", text: "Resolved a critical issue with great ownership.", chip: "Problem Solver" },
            ]).map((r, i) => (
              <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <Avatar className="w-7 h-7"><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 text-[9px]">{initials(r.from)}</AvatarFallback></Avatar>
                  <div className="text-[11.5px] font-semibold">{r.from}</div>
                  <Badge className="bg-fuchsia-50 text-fuchsia-700 border-0 text-[9px]">{r.chip}</Badge>
                </div>
                <div className="text-[11.5px] text-slate-600 italic">"{r.text}"</div>
              </div>
            ))}
          </Card>
        </TabsContent>
        {["team", "1-on-1 Notes", "performance", "recognition", "projects", "approvals", "documents", "skills", "activity"].map(t => (
          <TabsContent key={t} value={t} className="mt-4">
            <Card className="p-10 border-slate-200 bg-white text-center text-slate-500 text-sm">
              <Sparkles className="w-7 h-7 mx-auto text-slate-300 mb-2" />
              <span className="capitalize font-semibold text-slate-700">{t}</span> · backend pending
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
}

/* =================================================================== HR / ADMIN */
function AdminProfileSkin({ profile }) {
  return (
    <>
      <Card className="p-4 sm:p-5 border-violet-200 bg-gradient-to-r from-violet-50 to-white shadow-sm">
        <div className="flex items-center gap-4 flex-wrap">
          <Shield className="w-10 h-10 text-violet-600" />
          <div className="flex-1 min-w-0">
            <div className="font-bold font-[Outfit] text-slate-900">Profile Credibility · 92% Complete</div>
            <div className="text-[12px] text-slate-600">You're all set! Keep your profile updated for maximum impact.</div>
            <div className="mt-2 h-1.5 bg-slate-200 rounded overflow-hidden"><div className="h-full qh-grad-bg" style={{ width: "92%" }} /></div>
          </div>
          <button className="qh-btn btn-soft qh-btn-sm">View &amp; Improve</button>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MiniKPI icon={Users}       tone="violet"  label="Employees Managed" value={profile?.employees_managed ?? 0} hint={{ tone: "good", label: "↑ 6 this month" }} testid="kpi-emp" />
        <MiniKPI icon={Clock}       tone="amber"   label="Pending Cases"     value={profile?.pending_cases ?? 0} hint={{ tone: "good", label: "↓ 3 from last week" }} testid="kpi-cases" />
        <MiniKPI icon={Briefcase}   tone="rose"    label="Open Hiring Reqs"  value={profile?.open_hiring_requests ?? 0} hint={{ tone: "warn", label: "2 urgent" }} testid="kpi-hire" />
        <MiniKPI icon={Shield}      tone="emerald" label="Compliance"        value={`${profile?.compliance_pct ?? 96}%`} hint={{ tone: "good", label: "On track" }} testid="kpi-comp" />
        <MiniKPI icon={Activity}    tone="cyan"    label="Active Workflows"  value={profile?.active_workflows ?? 0} hint={{ tone: "warn", label: "5 require action" }} testid="kpi-flow" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-slate-100 flex-wrap h-auto">
          {["overview", "people ops", "compliance", "recruitment", "payroll", "documents", "workflows", "permissions", "skills", "activity"].map(t => (
            <TabsTrigger key={t} value={t} className="capitalize">{t}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-4 grid lg:grid-cols-3 gap-4">
          <Card className="p-5 border-slate-200 shadow-sm bg-white lg:col-span-2">
            <div className="font-bold font-[Outfit] text-slate-900 text-sm mb-1">Your Operational Ownership</div>
            <p className="text-[11px] text-slate-500 mb-3">Key areas under your responsibility</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: FileText,   label: "Policies Owned",     value: 18, sub: "of 24 policies",   bar: 75, tone: "violet" },
                { icon: Shield,     label: "Compliance Filings", value: 12, sub: "of 13 due",         bar: 92, tone: "emerald" },
                { icon: Users,      label: "Onboarding Cases",   value: 6,  sub: "In Progress",       bar: 60, tone: "violet" },
                { icon: Briefcase,  label: "Offboarding Cases",  value: 3,  sub: "1 awaiting action", bar: 45, tone: "amber" },
                { icon: Activity,   label: "Payroll Cycles",     value: 12, sub: "100% Completed",    bar: 100, tone: "emerald" },
                { icon: AlertTriangle, label: "Current Escalations", value: 4, sub: "High Priority",  bar: 80, tone: "rose" },
              ].map((c, i) => (
                <div key={i} className="p-3 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 rounded-lg ${c.tone === "emerald" ? "bg-emerald-50 text-emerald-600" : c.tone === "amber" ? "bg-amber-50 text-amber-600" : c.tone === "rose" ? "bg-rose-50 text-rose-600" : "bg-violet-50 text-violet-600"} flex items-center justify-center`}><c.icon className="w-3.5 h-3.5" /></div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex-1 truncate">{c.label}</div>
                  </div>
                  <div className="text-2xl font-bold font-[Outfit] tabular-nums">{c.value}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{c.sub}</div>
                  <div className="mt-2 h-1 bg-slate-100 rounded overflow-hidden"><div className={`h-full ${c.tone === "emerald" ? "bg-emerald-500" : c.tone === "amber" ? "bg-amber-500" : c.tone === "rose" ? "bg-rose-500" : "qh-grad-bg"}`} style={{ width: `${c.bar}%` }} /></div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 border-slate-200 shadow-sm bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold font-[Outfit] text-slate-900 text-sm">Recent Activity</div>
              <Link to="#" className="text-[11px] font-semibold text-violet-700 hover:underline">View all</Link>
            </div>
            {[
              { icon: FileText,   label: "Policy updated: Work From Home Policy", sub: "You updated the policy and sent for approval", time: "2h ago", tone: "violet" },
              { icon: Users,      label: "Onboarding case assigned",               sub: "Riya Mehra's onboarding is assigned to you",    time: "5h ago", tone: "emerald" },
              { icon: Shield,     label: "Compliance filing submitted",            sub: "PF ECR for April 2026 submitted successfully",  time: "1d ago", tone: "emerald" },
              { icon: AlertTriangle, label: "Escalation acknowledged",             sub: "Salary revision approval pending with Finance", time: "1d ago", tone: "rose" },
            ].map((a, i) => (
              <div key={i} className="py-2 border-b border-slate-100 last:border-0 flex items-start gap-2.5">
                <div className={`w-7 h-7 rounded-lg ${a.tone === "emerald" ? "bg-emerald-50 text-emerald-600" : a.tone === "rose" ? "bg-rose-50 text-rose-600" : "bg-violet-50 text-violet-600"} flex items-center justify-center shrink-0`}><a.icon className="w-3.5 h-3.5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold text-slate-900">{a.label}</div>
                  <div className="text-[11px] text-slate-500">{a.sub}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{a.time}</div>
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>
        {["people ops", "compliance", "recruitment", "payroll", "documents", "workflows", "permissions", "skills", "activity"].map(t => (
          <TabsContent key={t} value={t} className="mt-4">
            <Card className="p-10 border-slate-200 bg-white text-center text-slate-500 text-sm">
              <Sparkles className="w-7 h-7 mx-auto text-slate-300 mb-2" />
              <span className="capitalize font-semibold text-slate-700">{t}</span> · backend pending
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Card className="p-5 border-slate-200 shadow-sm bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold font-[Outfit] text-slate-900 text-sm">Quick Actions</div>
          <Link to="#" className="text-[11px] font-semibold text-violet-700 hover:underline">Customize</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {[
            { label: "Create Policy", icon: FileText, to: "/documents" },
            { label: "Assign Case", icon: Users, to: "/onboarding" },
            { label: "Start Onboarding", icon: Users, to: "/onboarding" },
            { label: "Raise Requisition", icon: Briefcase, to: "/recruitment" },
            { label: "Run Payroll", icon: Activity, to: "/payroll" },
            { label: "Upload Document", icon: FileText, to: "/documents" },
            { label: "Initiate Offboarding", icon: Users, to: "/offboarding" },
            { label: "Create Workflow", icon: Activity, to: "/workflow-studio" },
            { label: "Compliance Calendar", icon: CalendarDays, to: "/holidays" },
            { label: "Generate Report", icon: TrendingUp, to: "/audit-log" },
          ].map((q, i) => (
            <Link key={i} to={q.to} className="p-3 rounded-xl border border-slate-200 hover:border-violet-200 hover:bg-violet-50/40 transition flex flex-col items-center text-center gap-1.5">
              <q.icon className="w-4 h-4 text-violet-600" />
              <span className="text-[11.5px] font-semibold text-slate-700">{q.label}</span>
            </Link>
          ))}
        </div>
      </Card>
    </>
  );
}

/* =================================================================== ENTRY */
export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!user?.employee_id) return;
    api.get(`/employees/${user.employee_id}`).then(r => setProfile(r.data)).catch(() => {
      // Build a fallback profile from user object alone
      setProfile({
        id: user?.employee_id || "—",
        full_name: user?.full_name,
        email: user?.email,
        designation: user?.role?.replace("_", " "),
      });
    });
  }, [user?.employee_id]);

  const completion = useMemo(() => {
    if (!profile) return 0;
    const fields = ["full_name", "designation", "email", "phone", "address", "department_id", "joining_date", "skills"];
    const done = fields.filter(f => profile[f] && (Array.isArray(profile[f]) ? profile[f].length > 0 : true)).length;
    return Math.round((done / fields.length) * 100);
  }, [profile]);

  const role = user?.role || "employee";
  const isAdmin = ["super_admin", "hr_admin"].includes(role);
  const isManager = ["manager", "team_lead", "department_head"].includes(role);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 max-w-full overflow-x-hidden" data-testid="profile-page">
      <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
        <Link to="/" className="hover:text-violet-700 font-semibold">Dashboard</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-700 font-semibold">{isAdmin ? "Admin Profile" : isManager ? "Manager Profile" : "My Profile"}</span>
      </div>

      <HeaderCard profile={profile} role={role} onEdit={() => {}} />

      {isAdmin && <AdminProfileSkin profile={profile} />}
      {isManager && !isAdmin && <ManagerProfileSkin profile={profile} />}
      {!isAdmin && !isManager && <EmployeeProfileSkin profile={profile} completion={completion} />}
    </div>
  );
}
